import React from 'react';
import { t } from '@lingui/macro';
import { I18n } from '@lingui/react';
import { I18n as I18nCore } from '@lingui/core';
import { UnstyledButton } from '@oms/components-buttons';
import { Icon, light } from '@oms/ui-icon';

import * as styles from './styles';
import {
  UseInstrumentChartReturn,
  Compare,
  TechnicalIndicatorType,
} from '@oms/components-instrument-chart';

export type LegendProps = {
  seriesColors: string[];
  indicatorsColors: string[];
  removeCompare: UseInstrumentChartReturn['removeCompare'];
  removeAddition: UseInstrumentChartReturn['removeAddition'];
  removeTechnicalIndicator: UseInstrumentChartReturn['removeTechnicalIndicator'];
  instrument: UseInstrumentChartReturn['main']['instrument'];
  compares: UseInstrumentChartReturn['compares'];
  technicalIndicators: UseInstrumentChartReturn['technicalIndicators'];
  additions: UseInstrumentChartReturn['additions'];
};

export const Legend = ({
  instrument,
  seriesColors,
  indicatorsColors,
  compares,
  technicalIndicators,
  additions,
  removeCompare,
  removeAddition,
  removeTechnicalIndicator,
}: LegendProps) => {
  const handleRemoveCompare = ({ instrument }: Compare) => {
    if (!instrument?.itemSector) return;
    removeCompare(instrument.itemSector);
  };

  const handleRemoveAddition = (key: string) => {
    removeAddition(key);
  };

  const handleRemoveTechnicalIndicator = (key: TechnicalIndicatorType) => {
    removeTechnicalIndicator(key);
  };

  return (
    <I18n>
      {({ i18n }: { i18n: I18nCore }) => {
        const removeKey = t`Remove`;
        const removeString = i18n ? i18n._(removeKey) : removeKey.id;

        return (
          <ul css={styles.Legend}>
            <li css={styles.main} className="Legend__main">
              <i
                css={styles.labelCircle}
                style={{ backgroundColor: seriesColors[0] }}
              />
              {instrument && instrument.itemSector}
            </li>
            {Object.values(compares).map((item, i) => (
              <li
                key={item?.instrument?.itemSector}
                css={styles.label}
                className="Legend__instrument"
              >
                <UnstyledButton
                  onClick={() => handleRemoveCompare(item)}
                  title={`${removeString} ${item?.instrument?.itemSector}`}
                >
                  <i
                    css={styles.labelCircle}
                    style={{ backgroundColor: seriesColors[i + 1] }}
                  />
                  {item?.instrument?.itemSector}
                  <Icon className="removeIcon" icon={light.faTimes} />
                </UnstyledButton>
              </li>
            ))}
            {Object.values(technicalIndicators)
              .filter((item) => item)
              .map((indicator, i) => {
                const key = `${indicator?.indicator}:${indicator?.interval}`;
                return (
                  <li
                    key={key}
                    css={styles.label}
                    className="Legend__indicator"
                  >
                    <UnstyledButton
                      onClick={() => {
                        if (!indicator?.indicator) return;
                        handleRemoveTechnicalIndicator(indicator?.indicator);
                      }}
                      title={`${removeString} ${indicator?.indicator}`}
                    >
                      <i
                        css={styles.labelCircle}
                        style={{ backgroundColor: indicatorsColors[i] }}
                      />
                      {indicator?.indicator} ({indicator?.interval})
                      <Icon className="removeIcon" icon={light.faTimes} />
                    </UnstyledButton>
                  </li>
                );
              })}
            {Object.entries(additions)
              .filter(([_, item]) => item.active)
              .map(([key, item]) => (
                <li key={key} css={styles.label} className="Legend__addition">
                  <UnstyledButton
                    onClick={() => handleRemoveAddition(key)}
                    title={`${removeString} ${item.name}`}
                  >
                    <i
                      css={styles.labelCircle}
                      style={{ backgroundColor: item.color }}
                    />
                    {item.name}
                    <Icon className="removeIcon" icon={light.faTimes} />
                  </UnstyledButton>
                </li>
              ))}
          </ul>
        );
      }}
    </I18n>
  );
};
